import type { ReportBatchItemView, ReportBatchSummary } from './reportBatchTypes';

/** Running or queued with no bill finished yet — workers are still starting up. */
export function isStartingUp(b: ReportBatchSummary): boolean {
  return (b.status === 'running' || b.status === 'queued') && b.sent + b.failed + b.skipped === 0;
}

/** "Last activity 12s ago" / "Last activity 3m ago" from the batch heartbeat; null without one. */
export function lastActivityLabel(heartbeatAt: string | null, now: number): string | null {
  if (!heartbeatAt) return null;
  const at = Date.parse(heartbeatAt);
  if (Number.isNaN(at)) return null;
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  return seconds < 120 ? `Last activity ${seconds}s ago` : `Last activity ${Math.floor(seconds / 60)}m ago`;
}

/** The expanded bill list: bills being sent now, how many are waiting, and those not sent. */
export function itemBreakdown(items: ReportBatchItemView[]) {
  return {
    running: items.filter((i) => i.status === 'running'),
    pending: items.filter((i) => i.status === 'pending').length,
    notSent: items.filter((i) => i.status === 'failed' || i.status === 'skipped'),
  };
}

export function batchProgress(b: ReportBatchSummary, now: number) {
  const done = b.sent + b.failed + b.skipped;
  // M6: a cancelled batch's unsent bills will never be sent.
  const remaining = b.status === 'cancelled' ? 0 : Math.max(0, b.total - done);
  const pct = b.total ? Math.round((done / b.total) * 100) : 0;
  const start = b.startedAt ? Date.parse(b.startedAt) : null;
  const end = b.finishedAt ? Date.parse(b.finishedAt) : now;
  const minutes = start !== null ? (end - start) / 60_000 : 0;
  const perMinute = start !== null && minutes > 0 && done > 0 ? Math.round(done / minutes) : null;
  return { done, remaining, pct, perMinute };
}

/** Changes whenever a batch's outcome counts or status change — the cue to reload send status. */
export function progressSignature(list: ReportBatchSummary[]): string {
  return list.map((b) => `${b.id}:${b.status}:${b.sent}:${b.failed}:${b.skipped}`).join('|');
}
